import * as EventChunkUploadCommand from './event-chunk-upload-command';
import { DATA_UPLOAD_INTERVAL } from './constants';
import { ErrorNotifier } from './error-notifier';
import { EventChunk } from './event-chunk';
import { TriggeredEvent as EventPayload } from './triggered-event.types';
import { UserProfile } from './user-profiles/user-profile.types';
import { console } from './utility/console';
import { cancelIdleCallback, requestIdleCallback } from './utility/request-idle-callback';
import { InspectorPluginProxy } from './inspector-plugin-proxy';
import { isMalformedJson } from './utility/malformed-json-detector';

const multipleUploadTriggerTokens = [
  'd44db53c-69aa-4f54-a0e2-98b003077dfc', // https://dev-staging-admin.repro.io/apps/w8dqvm6r
  'e59f89e2-8cb4-4335-aee8-6ffbf2db8685', // https://admin.repro.io/apps/w3loyx37
  'ace0e5d4-bc8b-4900-bad9-3ed6bf874be2', // https://admin.repro.io/apps/86v0ro3m
  '46b0e29d-4376-4a05-bded-e77074d08144', // https://admin.repro.io/apps/n3mxlld2
  'fb514b62-3016-4045-be49-7399c3e359b3', // https://admin.repro.io/apps/r67kz1d5
  'e3fbeb1d-1301-4092-b295-e4cf5d219175', // https://admin.repro.io/apps/432oee65/
  '39f8044f-9728-43d1-ad03-5349f26db302', // https://admin.repro.io/apps/j6koqld1/
  '67cd26de-f4e6-4097-9a88-76fd29204288', // https://admin.repro.io/apps/o3ox5w34/
];

const defaultParams = {
  insightId: 0,
  uploadable: false,
  uploadURL: '',
  idfv: '',
  userAnnotation: '',
  snippetVersion: '',
  timeOffsetMills: 0,
  ipAddress: '',
  uploadInterval: DATA_UPLOAD_INTERVAL,
  token: '',
  shouldLaunchInspector: false,
  eventQueue: [] as EventPayload[],
  setupOptions: {} as Record<string, any>,
};

export class EventChunkUploader {
  // eventChunkの定期アップロードに使用する
  private timeoutIdToUpload!: number;
  private idleCallbackId!: number;
  private waitTimeUntilNextUpload: number;

  private uploadable: boolean;
  private uploadURL: string;
  private token: string;

  private eventChunk!: EventChunk;
  private inspectorPluginProxy: InspectorPluginProxy | null = null;

  public constructor({
    uploadable,
    uploadURL,
    insightId,
    idfv,
    userAnnotation,
    snippetVersion,
    timeOffsetMills,
    ipAddress,
    uploadInterval,
    token,
    shouldLaunchInspector,
    eventQueue,
    setupOptions = {},
  }: typeof defaultParams = defaultParams) {
    this.uploadable = uploadable;
    this.uploadURL = uploadURL;
    this.waitTimeUntilNextUpload = uploadInterval;
    this.token = token;

    // インターバルアップロード開始
    this.timeoutIdToUpload = window.setTimeout(() => {
      this.uploadPeriodically();
    }, this.waitTimeUntilNextUpload);

    // window離脱時のアップロード処理
    window.addEventListener('beforeunload', () => {
      this.uploadBeforeUnload();
    });

    // データの送り損じが解消されると、Web SDKを利用しているサイトすべての大きな影響が出ることが考えられるため
    // まずは対象を限定した状態でリリース・検証し、その結果をもって以後の方針を検討する
    // https://docs.google.com/document/d/1HAbn4XzfUf1Fg3bKy0_lElkwXeV7a5JxImGcsFZ0AyQ/edit
    if (multipleUploadTriggerTokens.includes(this.token)) {
      window.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden' && !this.eventChunk.isEmpty()) {
          this.uploadImmediately({ useBeacon: true, timing: 'visibilitychange' });
        }
      });

      window.addEventListener('pagehide', () => {
        if (!this.eventChunk.isEmpty()) {
          this.uploadImmediately({ useBeacon: true, timing: 'pagehide' });
        }
      });
    }

    this.eventChunk = new EventChunk({
      insightId,
      idfv,
      userAnnotation,
      snippetVersion,
      timeOffsetMills,
      ipAddress,
      setupOptions,
    });

    if (shouldLaunchInspector) {
      this.inspectorPluginProxy = new InspectorPluginProxy(eventQueue);
    }
  }

  public addEvent(event: EventPayload, isNewSession = false) {
    this.eventChunk.register({ key: 'events', chunk: event, isNewSession });

    if (this.inspectorPluginProxy != null) {
      this.inspectorPluginProxy.dispatch([event]);
    }
  }

  public setUserProfile({ type, key, value }: UserProfile) {
    const user = {
      [key]: { type, value },
    };
    this.eventChunk.register({ key: 'user', chunk: user });
  }

  public setSubscription(subscription: PushSubscriptionJSON) {
    const sendImmediately = this.eventChunk.isEmpty();
    this.eventChunk.registerSubscription(subscription);
    if (sendImmediately) {
      // subscription の送信漏れを減らすためにsubscriptionだけを即時アップロードする
      EventChunkUploadCommand.execute({
        token: this.token,
        url: this.uploadURL,
        useBeacon: false,
        timing: 'setSubscription',
        data: {
          context: this.toJSON.context,
          events: [],
          user: {},
        },
      });
    }
  }

  // setIntervalではなくsetTimeoutを使って、以前のeventChunkがuploadされたことを保証して再帰的にeventChunkをアップロード
  private uploadPeriodically() {
    this.idleCallbackId = requestIdleCallback(async () => {
      if (!this.eventChunk.isEmpty()) {
        await this.uploadImmediately({ useBeacon: false, timing: 'uploadPeriodically' }); // intervalでのuploadにはbeforeunloadに備えてxhrで送信する
      }
      // window.setTimeoutにしないと、TSの型が NodeJS.Timeout になる
      this.timeoutIdToUpload = window.setTimeout(() => {
        this.uploadPeriodically();
      }, this.waitTimeUntilNextUpload);
    });
  }

  private async uploadBeforeUnload() {
    // eventChunkの二重送信を防ぐためにインターバルを中止する
    window.clearTimeout(this.timeoutIdToUpload);
    // eventChunkの二重送信を防ぐためにidleCallbackを中止する
    cancelIdleCallback(this.idleCallbackId);
    if (!this.eventChunk.isEmpty()) {
      this.uploadImmediately({ useBeacon: true, timing: 'uploadBeforeUnload' }); // unload時にはbeaconを使用
    }
  }

  public get toJSON() {
    return this.eventChunk.toJSON;
  }

  public async reset() {
    if (!this.eventChunk.isEmpty()) await this.uploadImmediately({ useBeacon: false, timing: 'reset' });
    this.eventChunk.reset();

    if (this.inspectorPluginProxy != null) {
      this.inspectorPluginProxy.reset();
      this.inspectorPluginProxy = null;
    }
  }

  public flushEvents(timing: string): Promise<void | false> {
    return this.uploadImmediately({ useBeacon: false, timing });
  }

  private async uploadImmediately({
    useBeacon,
    timing,
  }: {
    useBeacon: boolean;
    timing: string;
  }): Promise<void | false> {
    if (!this.uploadable) {
      console.warn('Rejecting eventChunk upload since tracking is not allowed');
      return false;
    }

    try {
      if (isMalformedJson(this.eventChunk.toJSON.events)) {
        // 外部ライブラリにより EventChunk のフォーマットが書き換わってしまった場合、アップロードを中断する。
        // 不用意なフォーマット書き換えは Supplier 側の責任ではあるものの、現状では誤ったフォーマットの EventChunk を ClipWorker が処理できずエラーが発生するため。
        throw new Error('Rejecting eventChunk upload since data is malformed');
      }
    } catch (error: any) {
      console.warn(error.message);
      // 特定のページでのみ発生する場合でも頻度が高いことがありえ、その場合Rollbarのquotaを使い尽くしてしまう懸念があるので
      // サンプリングしてエラー通知頻度を抑える
      ErrorNotifier.notify({ error, extraPayload: this.eventChunk.toJSON, samplingRatio: 10000 });
      this.eventChunk.reset();
      return false;
    }

    // 送るべき情報がない場合はアップロードを中止する
    if (this.eventChunk.isEmpty()) {
      return false;
    }

    const chunkIterator = this.eventChunk.createChunkIteratorAndReset();
    /* eslint-disable no-restricted-syntax, no-await-in-loop */
    for (const data of chunkIterator) {
      await EventChunkUploadCommand.execute({
        token: this.token,
        url: this.uploadURL,
        useBeacon,
        timing,
        data,
      });
    }
    /* eslint-enable no-restricted-syntax, no-await-in-loop */
  }
}
